import React, { useRef, useState } from 'react';
import type { Reference } from '@apollo/client';
import { CloudinaryImage, generateSrcSet, IconButton, IconTrash16, IconTrash24, IconShare24, IconShare16, IconShareAndroid16, IconShareAndroid24, IconLink16, Modal, Button, Text, Box, StickerLabel } from '@stitch-fix/mode-react';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { removeSeeItOnMeLook } from '@stitch-fix/vision-shared';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useToast } from '../../../../utils/contexts/toast-provider';
import { useAndroidShareImage } from '../../../../utils/android/useAndroidShareImage';
import { isAndroidApp } from '../../../../utils/android';
import { themeMapping } from '../../../../utils/maps';
import { CurrentLook } from '../graphql/types';
import { useDeleteLookMutation } from '../graphql/deleteLook';
import { useVisionShareRecordCreateMutation } from '../graphql/visionShareRecordCreate';
import { GetLookbookGalleryDocument, GetLookbookGalleryQuery } from '../graphql/getLookbookGallery';
import { GetLookbookPreviousLooksDocument, GetLookbookPreviousLooksQuery } from '../graphql/getPreviousLooks';
import { useLookNavigationRestoration } from '../LookNavigationRestorationContext';
import styles from './LookImage.module.scss';
import zoomStyles from './look-image-zoom.module.scss';
type LookImageProps = {
  look: Pick<CurrentLook, 'id' | 'image' | 'stickerLabel'>;
  useSmallIcon?: boolean;
  onDelete?: () => void;
  children?: React.ReactNode;
  isShareUrlEnabled?: boolean;
  recipientCreditAmount?: string;
  isZoomEnabled?: boolean;
};
const VISION_SHARE_TITLE = 'See me in AI-powered styles';
const DEFAULT_RECIPIENT_CREDIT_AMOUNT = '$25';
const MOBILE_USER_AGENT_PATTERN = /Android|iPhone|iPad|iPod/i;
const getVisionShareText = (recipientCreditAmount = DEFAULT_RECIPIENT_CREDIT_AMOUNT) => `Check out my latest look from Stitch Fix and claim a ${recipientCreditAmount} credit!`;
const getVisionShareData = (url: string, recipientCreditAmount?: string) => ({
  title: VISION_SHARE_TITLE,
  text: getVisionShareText(recipientCreditAmount),
  url
});
const canUseNativeVisionShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function' && MOBILE_USER_AGENT_PATTERN.test(navigator.userAgent);
const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  const didCopy = document.execCommand('copy');
  document.body.removeChild(textArea);
  if (!didCopy) {
    throw new Error('Copy command failed');
  }
};
const LookImage = ({
  look,
  useSmallIcon = false,
  onDelete,
  children,
  isShareUrlEnabled = false,
  recipientCreditAmount = DEFAULT_RECIPIENT_CREDIT_AMOUNT,
  isZoomEnabled = false
}: LookImageProps) => {
  const router = useRouter();
  const recordLookNavigation = useLookNavigationRestoration();
  const [isZoomed, setIsZoomed] = useState(false);
  const {
    ref,
    EventProvider,
    reportSelectEvent
  } = useViewEvent({
    schema: 'vision_look_view',
    name: 'vision_look',
    vision_look_id: look.id
  });
  const {
    shareImage
  } = useAndroidShareImage();
  const isAndroid = isAndroidApp();
  const [createShareRecord, {
    loading: isSharing
  }] = useVisionShareRecordCreateMutation();
  const isShareInFlight = useRef(false);
  let ShareIconComponent: typeof IconShare16;
  if (useSmallIcon) {
    ShareIconComponent = isAndroid ? IconShareAndroid16 : IconShare16;
  } else {
    ShareIconComponent = isAndroid ? IconShareAndroid24 : IconShare24;
  }
  const shareIconTestId = isAndroid ? 'android-share-icon' : 'share-icon';
  const {
    addToast
  } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [shareModalUrl, setShareModalUrl] = useState<string | null>(null);
  const [deleteLook, {
    loading: isDeleting
  }] = useDeleteLookMutation({
    onCompleted: data => {
      const result = data.visionLookStatusUpdate?.result;
      if (result?.__typename === 'VisionLookStatusUpdateSuccess') {
        removeSeeItOnMeLook(look.id);
        addToast({
          message: 'Image deleted successfully',
          status: 'success'
        });
        onDelete?.();
      } else if (result?.__typename === 'VisionLookStatusUpdateError') {
        addToast({
          message: result.message || 'Failed to delete image',
          status: 'error'
        });
        setIsDeleteModalOpen(false);
      }
    },
    onError: () => {
      addToast({
        message: 'Something went wrong. Please try again.',
        status: 'error'
      });
      setIsDeleteModalOpen(false);
    },
    update(cache, {
      data
    }) {
      const result = data?.visionLookStatusUpdate?.result;
      if (result?.__typename !== 'VisionLookStatusUpdateSuccess') {
        return;
      }

      // Modify the normalized lists directly so the previous-looks pagination
      // merge policy cannot re-add the deleted reference.
      const galleryData = cache.readQuery<GetLookbookGalleryQuery>({
        query: GetLookbookGalleryDocument
      });
      const currentLookBook = galleryData?.client?.vision?.currentLookBook;
      const currentLookBookCacheId = currentLookBook ? cache.identify(currentLookBook) : undefined;
      if (currentLookBookCacheId) {
        cache.modify({
          id: currentLookBookCacheId,
          fields: {
            looks(existingLookRefs: readonly Reference[] | undefined, {
              readField
            }) {
              return (existingLookRefs ?? []).filter(lookRef => readField('id', lookRef) !== look.id);
            }
          }
        });
      }
      const previousLooksData = cache.readQuery<GetLookbookPreviousLooksQuery>({
        query: GetLookbookPreviousLooksDocument,
        variables: {
          page: 1
        }
      });
      const vision = previousLooksData?.client?.vision;
      const visionCacheId = vision ? cache.identify(vision) : undefined;
      if (visionCacheId) {
        cache.modify({
          id: visionCacheId,
          fields: {
            previousLooks(existingConnection, {
              readField
            }) {
              if (!existingConnection) return existingConnection;
              return {
                ...existingConnection,
                nodes: (existingConnection.nodes ?? []).filter((lookRef: Reference) => readField('id', lookRef) !== look.id)
              };
            }
          }
        });
      }
      setIsDeleteModalOpen(false);
    }
  });
  const handleDeleteImage = () => {
    setIsDeleteModalOpen(true);
  };
  const handleConfirmDelete = () => {
    deleteLook({
      variables: {
        input: {
          lookId: look.id,
          status: 'DELETED'
        }
      }
    });
  };
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };
  const handleCloseShareModal = () => {
    setShareModalUrl(null);
  };
  const handleCopyShareLink = async () => {
    if (!shareModalUrl) return;
    try {
      await copyTextToClipboard(shareModalUrl);
      addToast({
        message: 'Share link copied!',
        status: 'success'
      });
      handleCloseShareModal();
    } catch {
      addToast({
        message: 'Unable to copy link. Please try again.',
        status: 'error'
      });
    }
  };
  const downloadImage = async (pngUrl: string) => {
    try {
      const response = await fetch(pngUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vision-look-${look.id.split('-')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };
  const handleLegacyShare = () => {
    if (!look.image.baseUrl || !look.image.mediaPath) return;
    reportSelectEvent({
      schema: 'select',
      name: 'share_link',
      action_name: 'share_vision_image'
    });
    const pngUrl = `${look.image.baseUrl}f_png/${look.image.mediaPath}`;
    if (shareImage) {
      try {
        shareImage(pngUrl);
      } catch (err) {
        console.error('Android sharing failed', err);
        downloadImage(pngUrl);
      }
    } else {
      downloadImage(pngUrl);
    }
  };
  const handleShareUrlPath = async () => {
    if (isShareInFlight.current) return;
    isShareInFlight.current = true;
    try {
      const {
        data
      } = await createShareRecord({
        variables: {
          input: {
            lookId: look.id
          }
        }
      });
      const result = data?.visionShareRecordCreate?.result;
      if (result?.__typename !== 'VisionShareRecordCreateSuccess') {
        addToast({
          message: 'Unable to create share link. Please try again.',
          status: 'error'
        });
        return;
      }
      const shareUrl = result.shareUrl;
      reportSelectEvent({
        schema: 'select',
        name: 'share_link',
        action_name: 'share_vision_image'
      });
      if (canUseNativeVisionShare()) {
        try {
          await navigator.share(getVisionShareData(shareUrl, recipientCreditAmount));
          return;
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
        }
      }
      setShareModalUrl(shareUrl);
    } catch {
      addToast({
        message: 'Something went wrong. Please try again.',
        status: 'error'
      });
    } finally {
      isShareInFlight.current = false;
    }
  };

  // The Android bridge contract requires the image URL. New Android versions
  // can create the public Vision share record natively from this path.
  const handleSharingImage = shareImage || !isShareUrlEnabled ? handleLegacyShare : handleShareUrlPath;
  const imageSizes = {
    sm: 800
  };
  const imageTransforms = {
    ar: '2:3'
  };
  const lookUrl = `/gallery/look/${look.id}?returnUrl=${encodeURIComponent(router.asPath)}`;
  const handleLookNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isSameTabNavigation = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.currentTarget.target !== '_blank';
    if (isSameTabNavigation) {
      recordLookNavigation?.(look.id, event.currentTarget.getBoundingClientRect().top);
    }
  };
  const zoomImageSources = generateSrcSet({
    baseUrl: look.image.baseUrl,
    mediaPath: look.image.mediaPath,
    sizes: imageSizes,
    transforms: imageTransforms
  });
  const lookImage = <CloudinaryImage alt={look.image.altText} baseUrl={look.image.baseUrl} mediaPath={look.image.mediaPath} sizes={imageSizes} transforms={imageTransforms} width={2} height={3} objectFit="cover" />;
  return <EventProvider>
      <Box style={{
      display: 'flex',
      flexDirection: 'column'
    }} ref={ref}>
        <Box className={styles.lookImageRoot}>
          {isZoomEnabled ? <Box className={zoomStyles.zoom} data-testid="look-image-zoom">
              <ControlledZoom isZoomed={isZoomed} onZoomChange={setIsZoomed} classDialog={zoomStyles.zoom} zoomImg={{
            sizes: '100vw',
            src: zoomImageSources.fallback,
            srcSet: zoomImageSources.srcSet
          }}>
                {lookImage}
              </ControlledZoom>
            </Box> : <NextLink id={`gallery-look-${look.id}`} href={lookUrl} onClick={handleLookNavigation} style={{
          display: 'block'
        }}>
              {lookImage}
            </NextLink>}
          {look.stickerLabel && <Box className={styles.stickerLabelContainer}>
              <StickerLabel variant={themeMapping[look.stickerLabel.theme]}>
                {look.stickerLabel.label}
              </StickerLabel>
            </Box>}
          <Box mr={0.5} mt={0.5} className={styles.iconsContainer} style={{
          gap: useSmallIcon ? '0.25rem' : '0.5rem'
        }}>
            <IconButton aria-label="Share image" onClick={handleSharingImage} variant="overlay" disabled={isDeleting || isSharing}>
              <ShareIconComponent title="Share image" data-testid={shareIconTestId} />
            </IconButton>
            <IconButton aria-label="Delete image" onClick={handleDeleteImage} variant="overlay" disabled={isDeleting}>
              {useSmallIcon ? <IconTrash16 title="Delete image" /> : <IconTrash24 title="Delete image" />}
            </IconButton>
          </Box>
        </Box>
        {children && <Box mt={1}>{children}</Box>}
      </Box>
      <Modal isOpen={isDeleteModalOpen} heading="Delete image?" onClose={handleCancelDelete} size="small" variant="inset-small">
        <Box mt={1}>
          <Text setting="body-medium" spacingBottom>
            This image will be permanently deleted from your gallery.
          </Text>
          <Button variant="filled-statement" onClick={handleConfirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Confirm'}
          </Button>
        </Box>
      </Modal>
      <Modal isOpen={!!shareModalUrl} heading="Share your look" onClose={handleCloseShareModal} size="small" variant="inset-small">
        <Box mt={1}>
          <Text setting="body-medium" spacingBottom>
            {getVisionShareText(recipientCreditAmount)}
          </Text>
          <Button variant="filled-statement" width="fill" onClick={handleCopyShareLink} startIcon={<IconLink16 purpose="decorative" />}>
            Copy link
          </Button>
        </Box>
      </Modal>
    </EventProvider>;
};
export default LookImage;